import React, { useState } from 'react';
import { X, Plus, Trash, AlertCircle } from 'lucide-react';
import { segments, algorithmTypes, geos, optimizationModes, backoffers, secondClickOffers } from '../data/mockData';
interface SmartlinkFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}
export const SmartlinkForm: React.FC<SmartlinkFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [activeTab, setActiveTab] = useState('basics');
  const [formData, setFormData] = useState(initialData || {
    id: `sl_${Date.now()}`,
    status: 'active',
    name: '',
    description: '',
    offersInPool: [],
    strategyId: 'rpm',
    segments: [],
    geos: [],
    baseUrl: 'https://sinshift.com/sl/',
    urlParams: {
      click_id: '{click_id}',
      subid: '{subid}',
      geo: '{geo}',
      device: '{device}'
    },
    failover: {
      enabled: true,
      nextEligibleByPriority: true,
      protection404: true
    },
    caps: {
      daily: 5000,
      hourly: 500,
      spilloverRules: 'next_priority'
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const tabs = [{
    id: 'basics',
    label: 'Basics'
  }, {
    id: 'offers',
    label: 'Offers'
  }, {
    id: 'targeting',
    label: 'Targeting'
  }, {
    id: 'url',
    label: 'URL & Tracking'
  }, {
    id: 'caps',
    label: 'Caps & Failover'
  }, {
    id: 'preview',
    label: 'Preview'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const isCheckbox = type === 'checkbox';
    const checked = (e.target as HTMLInputElement).checked;
    // Handle nested properties
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: isCheckbox ? checked : value
          }
        }));
      } else if (parts.length === 3) {
        const [parent, middle, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [middle]: {
              ...prev[parent][middle],
              [child]: isCheckbox ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: isCheckbox ? checked : value
      }));
    }
  };
  const handleMultiSelect = (field: string, value: string, isSelected: boolean) => {
    setFormData(prev => {
      const currentArray = [...(prev[field] || [])];
      if (isSelected && !currentArray.includes(value)) {
        currentArray.push(value);
      } else if (!isSelected && currentArray.includes(value)) {
        const index = currentArray.indexOf(value);
        currentArray.splice(index, 1);
      }
      return {
        ...prev,
        [field]: currentArray
      };
    });
  };
  const handleUrlParamChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      urlParams: {
        ...prev.urlParams,
        [key]: value
      }
    }));
  };
  const handleAddUrlParam = () => {
    const newKey = prompt('Enter parameter name:');
    if (newKey && !formData.urlParams[newKey]) {
      setFormData(prev => ({
        ...prev,
        urlParams: {
          ...prev.urlParams,
          [newKey]: ''
        }
      }));
    }
  };
  const handleRemoveUrlParam = (key: string) => {
    setFormData(prev => {
      const newUrlParams = {
        ...prev.urlParams
      };
      delete newUrlParams[key];
      return {
        ...prev,
        urlParams: newUrlParams
      };
    });
  };
  const handleAddOffer = (offer: any) => {
    if (!formData.offersInPool.some((o: any) => o.offerId === offer.id)) {
      setFormData(prev => ({
        ...prev,
        offersInPool: [...prev.offersInPool, {
          offerId: offer.id,
          offerName: offer.name,
          weight: 50,
          priority: prev.offersInPool.length + 1
        }]
      }));
    }
  };
  const handleRemoveOffer = (offerId: string) => {
    setFormData(prev => ({
      ...prev,
      offersInPool: prev.offersInPool.filter((o: any) => o.offerId !== offerId)
    }));
  };
  const handleOfferWeightChange = (offerId: string, weight: number) => {
    setFormData(prev => ({
      ...prev,
      offersInPool: prev.offersInPool.map((o: any) => o.offerId === offerId ? {
        ...o,
        weight
      } : o)
    }));
  };
  const handleOfferPriorityChange = (offerId: string, priority: number) => {
    setFormData(prev => ({
      ...prev,
      offersInPool: prev.offersInPool.map((o: any) => o.offerId === offerId ? {
        ...o,
        priority
      } : o)
    }));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Validate basic fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.offersInPool.length === 0) {
      newErrors.offersInPool = 'At least one offer is required';
    }
    if (formData.segments.length === 0) {
      newErrors.segments = 'At least one segment is required';
    }
    if (formData.geos.length === 0) {
      newErrors.geos = 'At least one GEO is required';
    }
    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Base URL is required';
    }
    if (formData.caps.daily <= 0) {
      newErrors['caps.daily'] = 'Daily cap must be greater than 0';
    }
    if (formData.caps.hourly <= 0) {
      newErrors['caps.hourly'] = 'Hourly cap must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Find the first tab with an error and switch to it
      for (const tab of tabs) {
        const hasError = Object.keys(errors).some(key => {
          if (key.includes('.')) {
            const [parent] = key.split('.');
            return tab.id === parent;
          }
          if (key === 'offersInPool' && tab.id === 'offers') return true;
          if (key === 'segments' && tab.id === 'targeting') return true;
          if (key === 'geos' && tab.id === 'targeting') return true;
          return tab.id === key;
        });
        if (hasError) {
          setActiveTab(tab.id);
          break;
        }
      }
    }
  };
  // Get all available offers for the offer pool
  const availableOffers = [...backoffers, ...secondClickOffers];
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basics':
        return <div className="space-y-6" data-id="element-12763">
            <div data-id="element-12764">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12765">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12766">
                <option value="active" data-id="element-12767">Active</option>
                <option value="inactive" data-id="element-12768">Inactive</option>
              </select>
            </div>
            <div data-id="element-12769">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12770">
                Smartlink Name*
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., Premium Dating Smartlink" data-id="element-12771" />
              {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12772">
                  {errors.name}
                </p>}
            </div>
            <div data-id="element-12773">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12774">
                Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="Brief description of the smartlink" data-id="element-12775" />
            </div>
            <div data-id="element-12776">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12777">
                Optimization Strategy
              </label>
              <select name="strategyId" value={formData.strategyId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12778">
                {optimizationModes.map(mode => <option key={mode.id} value={mode.id} data-id="element-12779">
                    {mode.name}
                  </option>)}
              </select>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12780">
                {formData.strategyId === 'rpm' ? 'Optimizes for highest revenue per thousand impressions' : formData.strategyId === 'epc' ? 'Optimizes for highest earnings per click' : formData.strategyId === 'cvr' ? 'Optimizes for highest conversion rate' : 'Manually set weights and priorities'}
              </p>
            </div>
          </div>;
      case 'offers':
        return <div className="space-y-6" data-id="element-12781">
            <div data-id="element-12782">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12783">
                Offer Pool
              </h3>
              {errors.offersInPool && <p className="mb-2 text-sm text-red-600 dark:text-red-400" data-id="element-12784">
                  {errors.offersInPool}
                </p>}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-12785">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12786">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12787">
                    Selected Offers ({formData.offersInPool.length})
                  </h3>
                </div>
                <div className="p-6" data-id="element-12788">
                  {formData.offersInPool.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400" data-id="element-12789">
                      No offers selected. Add offers from the list below.
                    </p> : <div className="overflow-x-auto" data-id="element-12790">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12791">
                        <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-12792">
                          <tr data-id="element-12793">
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12794">
                              Offer Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12795">
                              Weight
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12796">
                              Priority
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12797">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12798">
                          {formData.offersInPool.map((offer: any) => <tr key={offer.offerId} data-id="element-12799">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-12800">
                                {offer.offerName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-12801">
                                <input type="number" value={offer.weight} onChange={e => handleOfferWeightChange(offer.offerId, parseInt(e.target.value))} className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white" min="1" max="100" data-id="element-12802" />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-12803">
                                <input type="number" value={offer.priority} onChange={e => handleOfferPriorityChange(offer.offerId, parseInt(e.target.value))} className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white" min="1" data-id="element-12804" />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12805">
                                <button onClick={() => handleRemoveOffer(offer.offerId)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" data-id="element-12806">
                                  <Trash size={16} data-id="element-12807" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12808">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12809">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12810">
                    Available Offers
                  </h3>
                </div>
                <div className="p-6" data-id="element-12811">
                  <div className="overflow-x-auto" data-id="element-12812">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12813">
                      <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-12814">
                        <tr data-id="element-12815">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12816">
                            Offer Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12817">
                            Type
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12818">
                            Vertical/Trigger
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12819">
                            Payout/Price
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12820">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12821">
                        {availableOffers.map(offer => <tr key={offer.id} data-id="element-12822">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-12823">
                              {offer.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12824">
                              {offer.id.startsWith('bo_') ? 'Backoffer' : 'Second-Click'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12825">
                              {offer.id.startsWith('bo_') ? offer.verticalId : offer.triggerId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12826">
                              {offer.id.startsWith('bo_') ? `$${offer.payoutAmount}` : offer.offerSource.type === 'internal' ? `$${offer.offerSource.discountedPrice}` : `$${offer.offerSource.payoutAmount}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12827">
                              <button onClick={() => handleAddOffer(offer)} className={`px-2 py-1 text-xs rounded ${formData.offersInPool.some((o: any) => o.offerId === offer.id) ? 'bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400' : 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:hover:bg-purple-900/30'}`} disabled={formData.offersInPool.some((o: any) => o.offerId === offer.id)} data-id="element-12828">
                                {formData.offersInPool.some((o: any) => o.offerId === offer.id) ? 'Added' : 'Add'}
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'targeting':
        return <div className="space-y-6" data-id="element-12829">
            <div data-id="element-12830">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12831">
                Segment Targeting*
              </label>
              <div className="border border-gray-300 dark:border-gray-700 rounded-md p-4" data-id="element-12832">
                {segments.map(segment => <div key={segment.id} className="flex items-center mb-3" data-id="element-12833">
                    <input type="checkbox" id={`segment-${segment.id}`} checked={formData.segments.includes(segment.id)} onChange={e => handleMultiSelect('segments', segment.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12834" />
                    <label htmlFor={`segment-${segment.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12835">
                      <span className="font-medium" data-id="element-12836">{segment.name}</span> -{' '}
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-12837">
                        {segment.description}
                      </span>
                    </label>
                  </div>)}
              </div>
              {errors.segments && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12838">
                  {errors.segments}
                </p>}
            </div>
            <div data-id="element-12839">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12840">
                GEO Targeting*
              </label>
              <div className="h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12841">
                {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12842">
                    <input type="checkbox" id={`geo-${geo.id}`} checked={formData.geos.includes(geo.id)} onChange={e => handleMultiSelect('geos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12843" />
                    <label htmlFor={`geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12844">
                      {geo.name} ({geo.code}) - {geo.tier}
                    </label>
                  </div>)}
              </div>
              {errors.geos && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12845">
                  {errors.geos}
                </p>}
            </div>
          </div>;
      case 'url':
        return <div className="space-y-6" data-id="element-12846">
            <div data-id="element-12847">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12848">
                Base URL*
              </label>
              <input type="text" name="baseUrl" value={formData.baseUrl} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.baseUrl ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="https://sinshift.com/sl/dating" data-id="element-12849" />
              {errors.baseUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12850">
                  {errors.baseUrl}
                </p>}
            </div>
            <div data-id="element-12851">
              <div className="flex justify-between items-center mb-2" data-id="element-12852">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-12853">
                  URL Parameters
                </label>
                <button type="button" onClick={handleAddUrlParam} className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors" data-id="element-12854">
                  Add Parameter
                </button>
              </div>
              <div className="space-y-2 border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12855">
                {Object.entries(formData.urlParams).map(([key, value]) => <div key={key} className="flex items-center gap-2" data-id="element-12856">
                    <div className="flex-1 flex items-center gap-2" data-id="element-12857">
                      <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[100px]" data-id="element-12858">
                        {key}:
                      </span>
                      <input type="text" value={value as string} onChange={e => handleUrlParamChange(key, e.target.value)} className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-12859" />
                    </div>
                    <button type="button" onClick={() => handleRemoveUrlParam(key)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" data-id="element-12860">
                      <X size={16} data-id="element-12861" />
                    </button>
                  </div>)}
                {Object.keys(formData.urlParams).length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-12862">
                    No URL parameters defined.
                  </p>}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md" data-id="element-12863">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2" data-id="element-12864">
                Available Macros
              </h4>
              <div className="grid grid-cols-2 gap-2" data-id="element-12865">
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12866">
                  {'{click_id}'} - Unique click identifier
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12867">
                  {'{subid}'} - Sub-ID for tracking
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12868">
                  {'{geo}'} - User's GEO code
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12869">
                  {'{device}'} - User's device type
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12870">
                  {'{os}'} - User's operating system
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12871">
                  {'{browser}'} - User's browser
                </div>
              </div>
            </div>
          </div>;
      case 'caps':
        return <div className="space-y-6" data-id="element-12872">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12873">
              <div data-id="element-12874">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12875">
                  Daily Cap*
                </label>
                <input type="number" name="caps.daily" value={formData.caps.daily} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['caps.daily'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12876" />
                {errors['caps.daily'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12877">
                    {errors['caps.daily']}
                  </p>}
              </div>
              <div data-id="element-12878">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12879">
                  Hourly Cap*
                </label>
                <input type="number" name="caps.hourly" value={formData.caps.hourly} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['caps.hourly'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12880" />
                {errors['caps.hourly'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12881">
                    {errors['caps.hourly']}
                  </p>}
              </div>
            </div>
            <div data-id="element-12882">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12883">
                Spillover Rules
              </label>
              <select name="caps.spilloverRules" value={formData.caps.spilloverRules} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12884">
                <option value="next_priority" data-id="element-12885">Next Priority</option>
                <option value="proportional" data-id="element-12886">Proportional to Weight</option>
                <option value="stop" data-id="element-12887">Stop (No Spillover)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12888">
                Defines what happens when an offer reaches its cap
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12889">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12890">
                Failover Settings
              </h3>
              <div className="space-y-4" data-id="element-12891">
                <div className="flex items-center" data-id="element-12892">
                  <input type="checkbox" id="failoverEnabled" name="failover.enabled" checked={formData.failover.enabled} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12893" />
                  <label htmlFor="failoverEnabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12894">
                    Enable Failover
                  </label>
                </div>
                {formData.failover.enabled && <>
                    <div className="flex items-center" data-id="element-12895">
                      <input type="checkbox" id="nextEligibleByPriority" name="failover.nextEligibleByPriority" checked={formData.failover.nextEligibleByPriority} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12896" />
                      <label htmlFor="nextEligibleByPriority" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12897">
                        Use Next Eligible Offer by Priority
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-12898">
                      <input type="checkbox" id="protection404" name="failover.protection404" checked={formData.failover.protection404} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12899" />
                      <label htmlFor="protection404" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12900">
                        404 Protection (Failover on Error)
                      </label>
                    </div>
                  </>}
              </div>
            </div>
          </div>;
      case 'preview':
        return <div className="space-y-6" data-id="element-12901">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12902">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12903">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12904">
                  Smartlink URL Preview
                </h3>
              </div>
              <div className="p-6" data-id="element-12905">
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md break-all font-mono text-sm" data-id="element-12906">
                  {formData.baseUrl}
                  {Object.entries(formData.urlParams).length > 0 && '?'}
                  {Object.entries(formData.urlParams).map(([key, value]) => `${key}=${value}`).join('&')}
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12907">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12908">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12909">
                  Smartlink Summary
                </h3>
              </div>
              <div className="p-6" data-id="element-12910">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6" data-id="element-12911">
                  <div data-id="element-12912">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12913">
                      Name
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12914">
                      {formData.name}
                    </dd>
                  </div>
                  <div data-id="element-12915">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12916">
                      Status
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12917">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${formData.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-12918">
                        {formData.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </dd>
                  </div>
                  <div data-id="element-12919">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12920">
                      Optimization Strategy
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12921">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${formData.strategyId === 'rpm' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : formData.strategyId === 'epc' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : formData.strategyId === 'cvr' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-12922">
                        {{
                        rpm: 'RPM Optimized',
                        epc: 'EPC Optimized',
                        cvr: 'CVR Optimized',
                        manual: 'Manual (Priority/Weight)'
                      }[formData.strategyId] || formData.strategyId}
                      </span>
                    </dd>
                  </div>
                  <div data-id="element-12923">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12924">
                      Offers in Pool
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12925">
                      {formData.offersInPool.length}
                    </dd>
                  </div>
                  <div data-id="element-12926">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12927">
                      Target Segments
                    </dt>
                    <dd className="mt-1 text-sm" data-id="element-12928">
                      <div className="flex flex-wrap gap-1" data-id="element-12929">
                        {formData.segments.map((segment: string) => <span key={segment} className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-12930">
                            {segment.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>)}
                      </div>
                    </dd>
                  </div>
                  <div data-id="element-12931">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12932">
                      Target GEOs
                    </dt>
                    <dd className="mt-1 text-sm" data-id="element-12933">
                      <div className="flex flex-wrap gap-1" data-id="element-12934">
                        {formData.geos.slice(0, 5).map((geo: string) => <span key={geo} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded" data-id="element-12935">
                            {geo.toUpperCase()}
                          </span>)}
                        {formData.geos.length > 5 && <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 rounded" data-id="element-12936">
                            +{formData.geos.length - 5} more
                          </span>}
                      </div>
                    </dd>
                  </div>
                  <div data-id="element-12937">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12938">
                      Daily Cap
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12939">
                      {formData.caps.daily.toLocaleString()}
                    </dd>
                  </div>
                  <div data-id="element-12940">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12941">
                      Hourly Cap
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12942">
                      {formData.caps.hourly.toLocaleString()}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12943">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12944">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12945">
                  Offers in Pool
                </h3>
              </div>
              <div className="p-6" data-id="element-12946">
                {formData.offersInPool.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400" data-id="element-12947">
                    No offers selected.
                  </p> : <ul className="space-y-3" data-id="element-12948">
                    {formData.offersInPool.map((offer: any) => <li key={offer.offerId} className="border border-gray-200 dark:border-gray-700 rounded-md p-3" data-id="element-12949">
                        <div className="flex justify-between" data-id="element-12950">
                          <div data-id="element-12951">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-12952">
                              {offer.offerName}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-12953">
                              ID: {offer.offerId}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3" data-id="element-12954">
                            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-12955">
                              <span className="font-medium" data-id="element-12956">Weight:</span>{' '}
                              {offer.weight}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-12957">
                              <span className="font-medium" data-id="element-12958">Priority:</span>{' '}
                              {offer.priority}
                            </div>
                          </div>
                        </div>
                      </li>)}
                  </ul>}
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12959">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-12960">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-12961">
          {mode === 'create' ? 'Create Smartlink' : 'Edit Smartlink'}
        </h2>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-12962">
          <X size={20} data-id="element-12963" />
        </button>
      </div>
      <div className="p-6" data-id="element-12964">
        <div className="mb-6 overflow-x-auto" data-id="element-12965">
          <div className="flex space-x-2" data-id="element-12966">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === tab.id ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-12967">
                {tab.label}
              </button>)}
          </div>
        </div>
        <form onSubmit={handleSubmit} data-id="element-12968">
          {renderTabContent()}
          <div className="mt-8 flex justify-end space-x-3" data-id="element-12969">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12970">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12971">
              {mode === 'create' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};